import { typography } from '@a-cloud-guru/themes';
import React from 'react';
import Head from 'next/head';
import { Spinner } from 'components/Spinner';
import MainLayout from 'layouts/MainLayout';
import styled from 'styled-components';
import ProfileContainer from 'components/ProfileContainer';
import UserProfileQuery from 'components/data/UserProfileQuery';
import GetCompletionCertificate from 'components/data/GetCompletionCertificate';
import Error from 'components/Error';
import { breakpoint } from 'styles/breakpoint';
import { withRouter } from 'next/router';
import { makeBuildOptimizedSrc } from 'components/ImageOptimizer';
import get from 'lodash.get';

class Home extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      certificateId: this.props.router.query.certificateId,
      isLA: get(this.props, 'router.query.certificateId', '').indexOf('-la') >= 0
    };
  }

  emptyHash() {
    return !this.state.certificateId;
  }

  render() {
    const { isLA, certificateId } = this.state;
    if (this.emptyHash()) {
      return (
        <MainLayout isLA={isLA}>
          <Spinner colour="#fff" size="3rem" />
        </MainLayout>
      );
    }

    return (
      <MainLayout isLA={isLA}>
        <GetCompletionCertificate certificateId={certificateId}>
          {({ loading, error, completionCertificate }) => {
            if (loading) {
              return <Spinner colour="#fff" size="3rem" />;
            }
            if (error) {
              return <Error />;
            }
            if (completionCertificate === null) {
              return <NotFound>This Certificate of Completion could not be verified.</NotFound>;
            }

            const { imageUrl, userId } = completionCertificate;

            const smImageUrl = makeBuildOptimizedSrc({
              width: 320,
              format: 'auto'
            })(imageUrl);
            const mdImageUrl = makeBuildOptimizedSrc({
              width: 920,
              format: 'auto'
            })(imageUrl);

            return (
              <UserProfileQuery userId={userId} isLA={isLA} certificateId={certificateId}>
                {({ loading, error, getUserProfile, getUserHandle }) => {
                  if (loading) {
                    return <Spinner colour="#fff" size="3rem" />;
                  }
                  if (error) {
                    return <Error />;
                  }

                  return (
                    <>
                      <Head>
                        <meta data-rh="true" property="og:image" content={mdImageUrl}></meta>
                        <meta name="twitter:card" content="summary_large_image"></meta>
                        <meta name="twitter:site" content="@acloudguru"></meta>
                        <meta name="twitter:image" content={mdImageUrl}></meta>
                        <meta name="twitter:title" content="Certificate of Completion - A Cloud Guru"></meta>
                        <meta name="twitter:text:title" content="Certificate of Completion - A Cloud Guru"></meta>
                        <meta name="og:title" content="Certificate of Completion - A Cloud Guru"></meta>
                        <title>Certificate of Completion - A Cloud Guru</title>
                      </Head>
                      <HeaderText>Certificate of Course Completion</HeaderText>
                      <SubHeadingText>
                        This is to certify that {getUserProfile.displayname} has completed the following course.
                      </SubHeadingText>
                      <SubText>Note: This is not an industry certification</SubText>
                      <Body>
                        <CertificationContainer>
                          <img
                            width="100%"
                            srcSet={`${smImageUrl} 480w, ${mdImageUrl} 920w`}
                            alt={`A Certificate of Completion awarded to ${getUserProfile.displayname} with Sam and Ryan Kroonenburg's signature`}
                          />
                        </CertificationContainer>
                        <ProfileContainer
                          profile={getUserProfile}
                          handle={(getUserHandle && getUserHandle.handle) || null}
                        />
                      </Body>
                    </>
                  );
                }}
              </UserProfileQuery>
            );
          }}
        </GetCompletionCertificate>
      </MainLayout>
    );
  }
}

const Body = styled.div`
  width: 100%;

  @media ${breakpoint.large} {
    display: flex;
  }
`;

const CertificationContainer = styled.div`
  @media ${breakpoint.large} {
    width: 67.5%;
  }
`;

const HeaderText = styled.h1`
  color: #fff;
  margin: 0;
  font-size: 1.5em;
  font-weight: 600;
  font-family: ${typography.brandFont};
  text-align: center;
  line-height: 1;
  margin-bottom: 0.15em;

  @media ${breakpoint.small} {
    font-size: 1.75em;
  }

  @media ${breakpoint.medium} {
    font-size: 2.75em;
  }
`;

const SubHeadingText = styled.h2`
  color: #fff;
  margin: 0;
  font-size: 1em;
  font-weight: 400;
  text-align: center;
  margin: 0.5em 0;

  @media ${breakpoint.medium} {
    font-size: 1.4em;
    margin: 0;
  }
`;

const SubText = styled.p`
  color: #fff;
  margin: 0;
  font-size: 0.75em;
  opacity: 0.5;
  margin: 0.5em 0 3.25em;
  text-align: center;

  @media ${breakpoint.medium} {
    font-size: 1em;
  }
`;

const NotFound = styled.h1`
  color: #fff;
`;

export default withRouter(Home);
